// ---------------------------------------------------------------------------
// Spec 4 / D1 — the ONLY bridge between the pure renderer-startup recorder and
// the analytics transport.
//
// The dependency direction is deliberate: this component imports the recorder,
// never the reverse. The recorder sits on the boot path being measured, so it
// must stay free of auth/depot/logger/PostHog imports; everything that needs
// those (the analytics context, boot_id correlation) lives here instead.
//
// Emission contract:
//  - The four document-lane `renderer_startup_phase` rows for EVERY document.
//  - The remaining `renderer_startup_phase` rows (operations, milestones,
//    attempt lanes) only for a deterministic sample of documents
//    (RENDERER_STARTUP_DETAIL_SAMPLE_PERCENT) and always under IRIS_E2E_TEST.
//    The local profile (readRendererDiagnostics) always holds every row.
//  - EXACTLY ONE `renderer_startup_measured` row per document.
//  - ZERO additional canonical boot_* events. This component never touches the
//    launch-scoped claims, the boot watchdog, or trackBootCompleted; the
//    one-boot-one-terminal invariant in bootstrap-analytics.ts is untouched.
//
// boot_id is attached HERE as correlation only. renderer_load_id remains the
// identity of the sample — one Electron launch legitimately contains many.
//
// Per rules: think-in-systems, code-comments-required, reference-rules-in-comments
// (ai.knowledge/global).
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { getBootId } from '#~/logger/index.js';
import {
  RENDERER_DOCUMENT_LANE,
  isRendererE2eRun,
  setRendererDiagnosticsSink
} from './renderer-startup.js';
import type { RendererDiagnosticRow, RendererDiagnosticsSink } from './renderer-startup.js';

/**
 * Share of documents whose fine-grained rows (operations, milestones, attempt
 * lanes) reach the transport. The summary row and the four document-lane phases
 * ship for every document — they are what the p50 dashboard reads. The rest is
 * ~40 rows per room refresh and hundreds per cold boot, only needed to attribute
 * a slow sample, so a deterministic sample of documents carries it.
 */
export const RENDERER_STARTUP_DETAIL_SAMPLE_PERCENT = 10;

// FNV-1a over the load id: cheap, deterministic, spreads UUIDs evenly.
const percentBucket = (value: string): number => {
  let hash = 0x81_1c_9d_c5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01_00_01_93) >>> 0;
  }
  return hash % 100;
};

/** Whether a document's detail rows are sent. `forceDetail` is the e2e override. */
export const isRendererStartupDetailSampled = (
  loadId: string,
  options: { readonly forceDetail?: boolean } = {}
): boolean =>
  options.forceDetail === true || percentBucket(loadId) < RENDERER_STARTUP_DETAIL_SAMPLE_PERCENT;

/** Rows that ship for every document: the summary and the disjoint document-lane phases. */
export const isRendererStartupSummaryRow = (row: RendererDiagnosticRow): boolean =>
  row.kind === 'document' || (row.span_kind === 'phase' && row.lane === RENDERER_DOCUMENT_LANE);

/**
 * Maps one recorder row to its analytics payload. Pure and exported so the test
 * asserts the exact shape that reaches the transport rather than re-deriving it.
 */
export const toRendererStartupEvent = (
  row: RendererDiagnosticRow,
  bootId: string,
  detailSampled: boolean = isRendererStartupDetailSampled(row.renderer_load_id)
): {
  name: typeof EVENT_NAMES.RENDERER_STARTUP_PHASE | typeof EVENT_NAMES.RENDERER_STARTUP_MEASURED;
  properties: EventProperties;
} => {
  if (row.kind === 'span') {
    return {
      name: EVENT_NAMES.RENDERER_STARTUP_PHASE,
      properties: {
        boot_id: bootId,
        renderer_load_id: row.renderer_load_id,
        span_kind: row.span_kind,
        lane: row.lane,
        phase: row.phase,
        sequence: row.sequence,
        start_ms: row.start_ms,
        end_ms: row.end_ms,
        duration_ms: row.duration_ms,
        outcome: row.outcome
      }
    };
  }

  return {
    name: EVENT_NAMES.RENDERER_STARTUP_MEASURED,
    properties: {
      boot_id: bootId,
      renderer_load_id: row.renderer_load_id,
      renderer_startup_duration_ms: row.renderer_startup_duration_ms,
      outcome: row.outcome,
      endpoint: row.endpoint,
      destination: row.destination,
      initial_route_kind: row.initial_route_kind,
      navigation_type: row.navigation_type,
      span_count: row.span_count,
      dropped_span_count: row.dropped_span_count,
      detail_sampled: detailSampled,
      nav_response_end_ms: row.nav_response_end_ms,
      nav_dom_content_loaded_ms: row.nav_dom_content_loaded_ms,
      nav_load_event_end_ms: row.nav_load_event_end_ms,
      loaded_js_count: row.loaded_js_count,
      loaded_js_transfer_bytes: row.loaded_js_transfer_bytes,
      loaded_js_encoded_bytes: row.loaded_js_encoded_bytes,
      loaded_js_decoded_bytes: row.loaded_js_decoded_bytes,
      loaded_js_sizes_available: row.loaded_js_sizes_available,
      long_task_count: row.long_task_count,
      long_task_total_ms: row.long_task_total_ms,
      long_task_max_ms: row.long_task_max_ms,
      timer_sample_count: row.timer_sample_count,
      timer_lateness_max_ms: row.timer_lateness_max_ms,
      timer_lateness_p95_ms: row.timer_lateness_p95_ms,
      hidden_ms: row.hidden_ms
    }
  };
};

export type RendererStartupAnalyticsProps = {
  /**
   * Sink installer. Defaults to the module singleton's, which is what production
   * always uses. It is injectable because the recorder is deliberately a
   * per-document singleton: a test covering several documents would otherwise
   * have to reset the module registry, which hands this component a SECOND copy
   * of React and breaks its hooks. Injecting an isolated recorder's installer
   * exercises the real effect instead.
   */
  readonly bindSink?: (sink: RendererDiagnosticsSink | undefined) => void;
};

/**
 * Attaches the recorder's sink for the lifetime of the mount. Renders nothing.
 *
 * Mounted inside BOTH AnalyticsProvider branches of <Root> so the tray window
 * and the main window behave identically. With transport disabled the recorder
 * keeps its local rows, so `readRendererDiagnostics()` still profiles.
 */
export const RendererStartupAnalytics = ({
  bindSink
}: RendererStartupAnalyticsProps = {}): null => {
  const { track } = useAnalytics();

  useEffect(() => {
    const install = bindSink ?? setRendererDiagnosticsSink;
    // e2e receipts correlate operation rows to the summary, so they need every row.
    const forceDetail = isRendererE2eRun();
    const sink = (row: RendererDiagnosticRow): void => {
      // A transport failure must never propagate into boot. The recorder also
      // guards its own call, but catching here keeps the failure attributable to
      // analytics rather than to the recorder.
      try {
        const detailSampled = isRendererStartupDetailSampled(row.renderer_load_id, {
          forceDetail
        });
        // An unsampled document ships only its summary rows; the full profile
        // stays readable locally through readRendererDiagnostics and the e2e probe.
        if (!detailSampled && !isRendererStartupSummaryRow(row)) {
          return;
        }
        const { name, properties } = toRendererStartupEvent(row, getBootId(), detailSampled);
        track(name, EVENT_CATEGORIES.APP_LIFECYCLE, properties);
      } catch {
        // Swallowed by design — see above.
      }
    };

    install(sink);
    // StrictMode double-invokes this effect. Detach/attach is safe: the recorder
    // tracks how many rows it has already delivered, so the second attach
    // replays nothing (renderer-startup.test.ts pins that).
    return () => install(undefined);
  }, [track, bindSink]);

  return null;
};
